import { useState } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import BrandLogo from '@/components/common/BrandLogo';
import { useDashboardIdentity } from '@/lib/auth/dashboard-context';
import { signout } from '@/helpers/authApi';

const navGroups = [
  {
    label: 'Overview',
    items: [{ label: 'Dashboard', to: '/dashboard', icon: 'ri-dashboard-line', tile: 'bg-[#3b82c4]', end: true }],
  },
  {
    label: 'Program',
    items: [
      { label: 'Members', to: '/dashboard/members', icon: 'ri-group-line', tile: 'bg-[#4f9fe0]' },
      { label: 'Points ledger', to: '/dashboard/transactions', icon: 'ri-history-line', tile: 'bg-[#3b6fe0]' },
      { label: 'Rewards', to: '/dashboard/rewards', icon: 'ri-gift-2-line', tile: 'bg-[#8b5cf6]' },
      { label: 'Redemptions', to: '/dashboard/redemptions', icon: 'ri-checkbox-circle-line', tile: 'bg-[#f0883a]' },
    ],
  },
  {
    label: 'Business',
    items: [
      { label: 'Sponsors', to: '/dashboard/sponsors', icon: 'ri-megaphone-line', tile: 'bg-[#ec4899]' },
      { label: 'Integrations', to: '/dashboard/integrations', icon: 'ri-plug-line', tile: 'bg-[#2f7a68]' },
    ],
  },
  {
    label: 'System',
    items: [{ label: 'Settings', to: '/dashboard/settings', icon: 'ri-settings-3-line', tile: 'bg-[#64748b]' }],
  },
];

const tile = 'w-8 h-8 shrink-0 flex items-center justify-center rounded-lg text-white';

function NavigationGroups({ collapsed, onNavigate }: { collapsed: boolean; onNavigate?: () => void }) {
  const identity = useDashboardIdentity();
  return (
    <nav className={`flex-1 overflow-y-auto py-3 flex flex-col ${collapsed ? 'px-2' : 'px-3'}`}>
      {navGroups.map((group) => {
        const items = group.items.filter((item) => identity?.dashboardPages?.includes(item.to));
        if (!items.length) return null;
        return (
          <div key={group.label} className="mb-3 flex flex-col gap-1">
            {collapsed
              ? <div className="w-8 h-px bg-background-200 mx-auto my-1" />
              : <span className="px-2 pb-1 text-[11px] font-bold uppercase tracking-[0.12em] text-foreground-400">{group.label}</span>}
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={'end' in item}
                onClick={onNavigate}
                title={collapsed ? item.label : undefined}
                className={({ isActive }) => `flex items-center gap-3 rounded-xl text-sm font-medium transition-colors ${collapsed ? 'justify-center p-1.5' : 'px-2 py-1.5'} ${isActive
                  ? 'bg-[#f1f3f6] text-[#2f8f4e] font-semibold'
                  : 'text-foreground-800 hover:bg-[#f6f7f9]'}`}
              >
                <span className={`${tile} ${item.tile}`}><i className={`${item.icon} text-base`} aria-hidden="true" /></span>
                {!collapsed && <span className="whitespace-nowrap">{item.label}</span>}
              </NavLink>
            ))}
          </div>
        );
      })}
    </nav>
  );
}

function AccountFooter({ collapsed }: { collapsed: boolean }) {
  const identity = useDashboardIdentity();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  async function handleSignout() {
    setBusy(true);
    try { await signout(); navigate('/login', { replace: true }); }
    finally { setBusy(false); }
  }
  const row = `w-full flex items-center gap-3 rounded-xl text-sm font-medium transition-colors cursor-pointer ${collapsed ? 'justify-center p-1.5' : 'px-2 py-1.5'}`;
  return (
    <div className={`py-3 border-t border-background-200 shrink-0 flex flex-col gap-1 ${collapsed ? 'px-2' : 'px-3'}`}>
      {!collapsed && identity?.email && <p className="px-2 pb-1 text-xs text-foreground-500 truncate" title={identity.email}>{identity.email}</p>}
      <Link to="/" title={collapsed ? 'Back to website' : undefined} className={`${row} text-foreground-800 hover:bg-[#f6f7f9]`}>
        <span className={`${tile} bg-[#94a3b8]`}><i className="ri-arrow-left-line text-base" aria-hidden="true" /></span>
        {!collapsed && <span className="whitespace-nowrap">Back to website</span>}
      </Link>
      <button type="button" disabled={busy} onClick={handleSignout} title={collapsed ? 'Sign out' : undefined} className={`${row} text-[#c62f2f] hover:bg-[#fdf3f3] disabled:opacity-60`}>
        <span className={`${tile} bg-[#d13b3b]`}><i className="ri-logout-box-r-line text-base" aria-hidden="true" /></span>
        {!collapsed && <span className="whitespace-nowrap">Sign out</span>}
      </button>
    </div>
  );
}

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

export default function Sidebar({ open, onClose, collapsed, onToggleCollapse }: SidebarProps) {
  return (
    <>
      <aside className={`hidden lg:flex relative h-full shrink-0 flex-col bg-white border-r border-background-200 transition-[width] duration-300 ease-in-out ${collapsed ? 'w-[68px]' : 'w-64'}`}>
        <button
          type="button"
          onClick={onToggleCollapse}
          className="absolute -right-3.5 top-16 -translate-y-1/2 z-50 w-7 h-7 bg-white hover:bg-background-100 border border-background-300 rounded-full flex items-center justify-center text-foreground-600 cursor-pointer"
          title={collapsed ? 'Expand sidebar (Ctrl+B)' : 'Collapse sidebar (Ctrl+B)'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <i className={`${collapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-left-s-line'} text-sm`} />
        </button>
        <div className={`h-16 flex items-center border-b border-background-200 shrink-0 ${collapsed ? 'px-3 justify-center' : 'px-5'}`}>
          <Link to="/" className="flex items-center gap-2.5 overflow-hidden" title="LoyaltyBridge">
            <BrandLogo className="w-9 h-9" />
            {!collapsed && <span className="font-heading font-bold text-base text-foreground-950 whitespace-nowrap">LoyaltyBridge</span>}
          </Link>
        </div>
        <NavigationGroups collapsed={collapsed} />
        <AccountFooter collapsed={collapsed} />
      </aside>

      {open && <div onClick={onClose} className="fixed inset-0 z-40 bg-foreground-950/40 lg:hidden" aria-hidden="true" />}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-background-200 flex flex-col transform transition-transform duration-300 ease-in-out lg:hidden ${open ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="h-16 flex items-center justify-between border-b border-background-200 px-5 shrink-0">
          <Link to="/" onClick={onClose} className="flex items-center gap-2.5" title="LoyaltyBridge">
            <BrandLogo className="w-9 h-9" />
            <span className="font-heading font-bold text-base text-foreground-950">LoyaltyBridge</span>
          </Link>
          <button type="button" onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-500 hover:bg-background-100 cursor-pointer" aria-label="Close menu">
            <i className="ri-close-line text-lg" />
          </button>
        </div>
        <NavigationGroups collapsed={false} onNavigate={onClose} />
        <AccountFooter collapsed={false} />
      </aside>
    </>
  );
}
